import { z } from 'zod';
import { emailSchema, passwordSchema, totpCodeSchema } from './common.js';
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Le mot de passe est requis.'),
    totpCode: totpCodeSchema.optional(),
});
export const registerCompanySchema = z
    .object({
    email: emailSchema,
    password: passwordSchema,
    passwordConfirm: z.string(),
    raisonSociale: z.string().trim().min(2).max(200),
    contactNom: z.string().trim().min(1).max(100),
    contactPrenom: z.string().trim().min(1).max(100),
    contactFonction: z.string().trim().min(1).max(150),
    contactTelephone: z
        .string()
        .trim()
        .regex(/^\+?[\d\s().-]{6,20}$/, 'Numéro de téléphone invalide.'),
    consentLegalTerms: z.literal(true, {
        errorMap: () => ({ message: 'Vous devez accepter les conditions générales.' }),
    }),
})
    .refine((d) => d.password === d.passwordConfirm, {
    message: 'Les mots de passe ne correspondent pas.',
    path: ['passwordConfirm'],
});
// ─── Inscription publique candidat (Vague A) ──────────────────────────────
//
// Le candidat peut désormais s'inscrire librement, sans invitation.
// Selon son sous-type il fournit des infos différentes :
//   - lyceen / etudiant : formationCode requis (établissement scolaire)
//   - actif / demandeur_emploi / reconversion : currentJob / currentCompany
//
// Le mot de passe est défini directement (pas de mail d'activation pour
// la v1 — on log le user immédiatement après création).
export const candidateTypeSchema = z.enum([
    'lyceen',
    'etudiant',
    'actif',
    'demandeur_emploi',
    'reconversion',
]);
export const jobSearchTypeSchema = z.enum([
    'stage',
    'alternance',
    'apprentissage',
    'cdd',
    'cdi',
    'interim',
    'extra',
    'freelance',
    'autre',
]);
const candidateBase = z
    .object({
    email: emailSchema,
    password: passwordSchema,
    passwordConfirm: z.string(),
    civilite: z.enum(['M', 'Mme', 'X']).optional().or(z.literal('')),
    prenom: z.string().trim().min(1).max(100),
    nom: z.string().trim().min(1).max(100),
    candidateType: candidateTypeSchema,
    linkedinUrl: z
        .string()
        .trim()
        .url('URL LinkedIn invalide.')
        .max(500)
        .optional()
        .or(z.literal('')),
    lookingForJobTypes: z.array(jobSearchTypeSchema).max(9).default([]),
    consentLegalTerms: z.literal(true, {
        errorMap: () => ({ message: 'Vous devez accepter les conditions générales.' }),
    }),
})
    .refine((d) => d.password === d.passwordConfirm, {
    message: 'Les mots de passe ne correspondent pas.',
    path: ['passwordConfirm'],
});
// Pour les lycéens / étudiants, on demande la formation.
export const registerScolaireSchema = candidateBase.and(z.object({
    formationCode: z.string().trim().min(1, 'Sélectionnez votre formation.'),
}));
// Pour les actifs / demandeurs / reconversion, on demande le poste actuel.
// `currentJob` est le titre du poste (ou « Demandeur d'emploi »), `currentCompany`
// l'entreprise actuelle (vide si demandeur).
export const registerNonScolaireSchema = candidateBase.and(z.object({
    currentJob: z.string().trim().min(1, 'Précisez votre poste actuel.').max(150),
    currentCompany: z.string().trim().max(200).optional().or(z.literal('')),
}));
/**
 * Schéma "discriminé" qui choisit la bonne variante selon `candidateType`.
 * Côté serveur on appelle `parseRegisterCandidate(body)` pour obtenir le
 * type approprié.
 */
/**
 * Inscription "lite" — email + mot de passe + CGU uniquement.
 *
 * Le candidat est créé en mode draft, rattaché par défaut à
 * `HORS-FORMATION` et `candidateType=etudiant`. Toutes les autres
 * informations (prénom, nom, formation, type de profil) sont remplies
 * après login via l'onboarding CV + IA + profil.
 *
 * Justification : on lève les frictions d'inscription pour que le
 * candidat arrive directement sur l'onboarding CV. Le profil reste
 * invisible aux entreprises tant qu'il n'est pas complet.
 */
export const registerLiteSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    consentLegalTerms: z.literal(true, {
        errorMap: () => ({ message: 'Vous devez accepter les conditions générales.' }),
    }),
});
export function parseRegisterCandidate(input) {
    const baseParsed = candidateBase.safeParse(input);
    if (!baseParsed.success)
        throw baseParsed.error;
    const t = baseParsed.data.candidateType;
    if (t === 'lyceen' || t === 'etudiant') {
        return { kind: 'scolaire', data: registerScolaireSchema.parse(input) };
    }
    return { kind: 'non_scolaire', data: registerNonScolaireSchema.parse(input) };
}
export const acceptInvitationSchema = z
    .object({
    password: passwordSchema,
    passwordConfirm: z.string(),
    civilite: z.enum(['M', 'Mme', 'X']).optional(),
    nom: z.string().trim().min(1).max(100),
    prenom: z.string().trim().min(1).max(100),
    consentLegalTerms: z.literal(true, {
        errorMap: () => ({ message: 'Vous devez accepter les conditions générales.' }),
    }),
})
    .refine((d) => d.password === d.passwordConfirm, {
    message: 'Les mots de passe ne correspondent pas.',
    path: ['passwordConfirm'],
});
export const forgotPasswordSchema = z.object({
    email: emailSchema,
});
export const resetPasswordSchema = z.object({
    token: z.string().min(20),
    password: passwordSchema,
    passwordConfirm: z.string(),
}).refine((d) => d.password === d.passwordConfirm, {
    message: 'Les mots de passe ne correspondent pas.',
    path: ['passwordConfirm'],
});
export const setupTotpSchema = z.object({
    totpCode: totpCodeSchema,
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
});
//# sourceMappingURL=auth.js.map